import { Box, Fade, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { IconAlertCircle } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container } from '../components/atomic';
import SEO from '../components/SEO';
import SecureEmail from '../components/SecureEmail';
import { StyledButton } from '../components/ui';
import { LegalPageLayout } from '../layouts';

/**
 * 500 Server Error page component
 *
 * Dark navy aesthetic matching the Homepage/About/Product pages:
 * - Full-height dark navy background
 * - Error icon and 500 number in warning red (subdued against dark bg)
 * - White text for headings and body
 * - Consistent button styling with site-wide patterns
 * - Support contact information with accent green styling
 *
 * @returns {JSX.Element} 500 error page
 */
function ServerError() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  const handleRefresh = () => {
    window.location.reload();
  };

  return (
    <LegalPageLayout>
      <SEO
        title="Server Error"
        description="EllaFin is experiencing technical difficulties. Please try again or contact support if the problem persists."
        noIndex={true}
      />

      <Box
        sx={{
          minHeight: 'calc(100vh - 84px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#1e2140',
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center' }}>
            <Fade in={isLoaded} timeout={600}>
              <Box>
                {/* Error Icon */}
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
                  <IconAlertCircle
                    size={80}
                    stroke={1.5}
                    color={alpha('#ff6b6b', 0.8)}
                  />
                </Box>

                {/* 500 Number */}
                <Typography
                  variant="h1"
                  sx={{
                    fontSize: { xs: '4rem', sm: '5rem', md: '6rem' },
                    fontWeight: 900,
                    color: '#ff6b6b',
                    lineHeight: 0.9,
                    mb: 2,
                    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                  }}
                >
                  500
                </Typography>
              </Box>
            </Fade>

            <Fade in={isLoaded} timeout={800}>
              <Box>
                {/* Error Message */}
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
                    fontWeight: 600,
                    color: '#ffffff',
                    mb: 2,
                    fontFamily: '"Playfair Display", serif',
                  }}
                >
                  Something Went Wrong
                </Typography>
              </Box>
            </Fade>

            <Fade in={isLoaded} timeout={1000}>
              <Box>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: '1.125rem',
                    color: alpha('#ffffff', 0.7),
                    mb: 4,
                    maxWidth: '500px',
                    mx: 'auto',
                    lineHeight: 1.6,
                  }}
                >
                  We're experiencing some technical difficulties. Our team has
                  been notified and is working to resolve the issue. Please try
                  again in a few moments.
                </Typography>

                {/* Action Buttons */}
                <Box
                  sx={{
                    display: 'flex',
                    gap: 2,
                    justifyContent: 'center',
                    flexWrap: 'wrap',
                    mb: 4,
                  }}
                >
                  <StyledButton
                    variant="contained"
                    size="large"
                    onClick={handleRefresh}
                    sx={{ minWidth: '160px' }}
                  >
                    Try Again
                  </StyledButton>

                  <StyledButton
                    variant="outlined"
                    size="large"
                    onClick={() => navigate('/')}
                    sx={{
                      minWidth: '160px',
                      color: '#ffffff',
                      borderColor: alpha('#ffffff', 0.3),
                      '&:hover': {
                        borderColor: theme.palette.secondary.main,
                        color: theme.palette.secondary.main,
                      },
                    }}
                  >
                    Go Home
                  </StyledButton>
                </Box>

                {/* Support Information */}
                <Box
                  sx={{
                    pt: 4,
                    borderTop: `1px solid ${alpha('#ffffff', 0.1)}`,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ color: alpha('#ffffff', 0.5), mb: 1 }}
                  >
                    If this problem persists, please contact our support team
                  </Typography>

                  <SecureEmail
                    emailKey="support"
                    sx={{
                      color: theme.palette.secondary.main,
                      textDecoration: 'underline',
                      fontSize: '1rem',
                      '&:hover': { color: theme.palette.secondary.light },
                    }}
                    fallbackText="Support email (JavaScript required)"
                  />
                </Box>

                {/* Error Code for Support */}
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    mt: 3,
                    color: alpha('#ffffff', 0.3),
                    fontSize: '0.875rem',
                  }}
                >
                  Error Code: SRV-500-{Date.now().toString().slice(-6)}
                </Typography>
              </Box>
            </Fade>
          </Box>
        </Container>
      </Box>
    </LegalPageLayout>
  );
}

export default ServerError;
